import React, { useState } from 'react';
import { CircularProgress, Badge, Tooltip } from '@mui/material';
import { Button } from '@components/primitives/Button';
import { useOnboardingSteps, useFeatureFlags, useUserInfo } from '@features/user';
import GoogleCalendarOnboarding from './GoogleCalendarOnboarding';
import { initiateGoogleIntegration } from '../../../services/GoogleCalendarService';
import { useEventTracking } from '@services/statsig';

interface SyncGoogleCalendarProps {
  disabled?: boolean;
  variant?: 'default' | 'fullWidth';
}

const SyncGoogleCalendar: React.FC<SyncGoogleCalendarProps> = ({
  disabled = false,
  variant = 'default'
}) => {
  const onboardingSteps = useOnboardingSteps();
  const featureFlags = useFeatureFlags();
  const userInfo = useUserInfo();
  const { googleCalendar } = useEventTracking();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewBadge, setShowNewBadge] = useState<boolean>(
    !localStorage.getItem('syncGoogleCalendarBadgeClicked'),
  );

  // Check if onboarding has been completed
  const onboardingCompleted = onboardingSteps?.google_calendar_onboarding_completed;

  // Return null if the google_calendar_sync feature flag is not enabled or if the user is already integrated
  if (!featureFlags?.google_calendar_sync || userInfo?.google_calendar_integrated) {
    return null;
  }

  const handleConnectCalendar = async () => {
    setLoading(true);
    try {
      await initiateGoogleIntegration();
      // The user will be redirected to Google's auth page, so no need to setLoading(false)
    } catch (error) {
      console.error('Failed to initiate Google Calendar integration:', error);
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    // Track connection initiation
    googleCalendar.connectInitiated({ integration_source: 'appointments_page' });

    // Hide the badge when clicked
    if (showNewBadge) {
      localStorage.setItem('syncGoogleCalendarBadgeClicked', 'true');
      setShowNewBadge(false);
    }

    if (!onboardingCompleted) {
      // Show onboarding dialog if not completed yet
      setShowOnboarding(true);
    } else {
      // Directly initiate Google Calendar integration
      handleConnectCalendar();
    }
  };

  const handleOnboardingComplete = (proceed: boolean) => {
    setShowOnboarding(false);
    if (proceed) {
      // Initiate Google Calendar integration if user completed onboarding
      handleConnectCalendar();
    }
  };

  // Reusable button component to avoid duplication
  const SyncButton = (
    <Tooltip title="Sync Google Calendar" arrow>
      <Button
        variant="outlined"
        color="inherit"
        size="large"
        onClick={handleButtonClick}
        disabled={disabled || loading}
        fullWidth={variant === 'fullWidth'}
        startIcon={
          loading ? (
            <CircularProgress size={16} />
          ) : (
            <img
              src="/new-feature-images/google_calendar.png"
              alt="Google Calendar Icon"
              style={{ width: 20, height: 20 }}
            />
          )
        }
        sx={{
          color: '#535976',
          borderColor: 'rgba(0, 13, 77, 0.45)',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: '#535976' },
          ...(variant === 'fullWidth' && {
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
          }),
        }}
      >
        {loading ? 'Connecting...' : 'Sync Calendar'}
      </Button>
    </Tooltip>
  );

  return (
    <>
      {showNewBadge ? (
        <Badge
          color="info"
          badgeContent="NEW"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.6rem',
              padding: '0 6px',
              fontWeight: 'bold',
              transform: 'scale(1) translate(30%, -60%)',
            },
          }}
        >
          {SyncButton}
        </Badge>
      ) : (
        SyncButton
      )}

      {/* Onboarding Dialog */}
      <GoogleCalendarOnboarding
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />
    </>
  );
};

export default SyncGoogleCalendar;
